import React, { useContext, useEffect, useState } from 'react';
import { useSessionTimer } from '../hooks/useSessionTimer';
import { useUserAchievements } from '../hooks/useUserAchievements';
import arrowLeft from '../images/arrow-left.svg';
import btnClose from '../images/close-line.svg';
import bookCover from '../images/empty-book.svg';
import btnLeft from '../images/minus-line.svg';
import btnRight from '../images/plus-line.svg';
import iconCalendar from '../images/calendar-line.svg';
import iconClock from '../images/clock-line.svg';
import iconSpeed from '../images/stats-line.svg';
import { PersistentContext } from './PersistentContext';
import { formatTime, updateProtocol } from '../utils/functions';
import { useTranslation } from '../i18n';
import SessionForm from './SessionForm';
import BookInfo from './BookInfo';
import Session from './Session';
import ShareableCard from './SharableCard';
import { addSessionToBook } from '../utils/firebaseService';
import moment from 'moment';
import { analytics } from '../firebaseConfig';
import { logEvent } from 'firebase/analytics';

const TopBar = ({
  user,
  fetchBooks,
  sessions,
  fetchUserData,
  showNotification,
}) => {
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const readSpeed = {
    slow: 30,
    medium: 45,
    fast: 60,
  };

  const { globalState, setGlobalState } = useContext(PersistentContext);
  const [quote, setQuote] = useState('');
  const [summary, setSummary] = useState('');
  const [review, setReview] = useState('');
  const [rating, setRating] = useState('');
  const [currentBook, setCurrentBook] = useState({});
  const [saving, setSaving] = useState(false);

  const [date, setDate] = useState(moment(yesterday).format('YYYY-MM-DD'));
  const [sessionTime, setTime] = useState(
    moment(yesterday).set({ hour: 22, minute: 0 }).format('HH:mm'),
  );
  const [speed, setSpeed] = useState(parseInt(readSpeed.medium));

  const { t, weekstart } = useTranslation();
  const { checkAchievements } = useUserAchievements(showNotification, t);

  useEffect(() => {
    const updatedBook = {
      title: user?.currentlyReading?.title,
      author_name: user?.currentlyReading?.author_name,
      number_of_pages: user?.currentlyReading?.number_of_pages,
      pages: user?.currentlyReading?.pages,
      time: user?.currentlyReading?.time,
      publish_date: user?.currentlyReading?.publish_date,
      publisher: user?.currentlyReading?.publisher,
      isbn: user?.currentlyReading?.isbn,
      cover_url: user?.currentlyReading?.cover_url,
      bookID: user?.currentlyReading?.bookID,
    };

    setCurrentBook(updatedBook);
  }, [user]);

  const time = globalState.duration || 0;
  const isActive = globalState.activeSession || false;
  const isManual = globalState.manualSession || false;
  const isRunning = globalState.isRunning || false;
  const startTime = globalState.startTime || null;
  const sessionDuration = globalState.sessionDuration || 0;
  const endPage =
    globalState.endPage || (currentBook.pages ? currentBook.pages : 0);

  const noCurrentBook =
    !currentBook.title ||
    !currentBook.author_name ||
    !currentBook.number_of_pages;

  const hasPageSpeed = currentBook.pages > 0 && currentBook.time > 0;
  const pagesPerHour = hasPageSpeed
    ? Math.round((currentBook.pages / currentBook.time) * 60 * 60)
    : speed;
  const manualDuration = Math.round(
    (endPage - (currentBook.pages ? currentBook.pages : 0)) /
      (pagesPerHour / 60 / 60),
  );

  const { share } = globalState;

  useSessionTimer(isRunning, startTime, sessionDuration, setGlobalState);

  const handleQuoteChange = (event) => {
    setQuote(event.target.value);
  };

  const handleSummaryChange = (event) => {
    setSummary(event.target.value);
  };

  const handleReviewChange = (event) => {
    setReview(event.target.value);
  };

  const handleDateChange = (event) => {
    setDate(event.target.value);
  };

  const handleTimeChange = (event) => {
    setTime(event.target.value);
  };

  const handleSpeedChange = (value) => {
    setSpeed(parseInt(value));
  };

  const handleStart = () => {
    if (noCurrentBook) {
      // TODO: Add overlay book select
      showNotification(t.nobookreadMessage);
      return false;
    }
    const startTime = new Date().getTime();
    setGlobalState((prevState) => ({
      ...prevState,
      isRunning: true,
      activeSession: true,
      endPage: prevState.endPage,
      startTime: startTime || new Date().getTime(),
      sessionDuration: sessionDuration || 0,
    }));

    logEvent(
      analytics,
      sessionDuration > 0 ? 'reading_session_resume' : 'reading_session_start',
    );
  };

  const handleStop = () => {
    const now = new Date().getTime();
    const currentSessionDuration = Math.floor((now - startTime) / 1000);

    setGlobalState((prevState) => ({
      ...prevState,
      isRunning: false,
      sessionDuration: prevState.sessionDuration + currentSessionDuration,
      duration: prevState.sessionDuration + currentSessionDuration, // Ensure duration is up to date when stopping
    }));
  };

  const handleClickReset = () => {
    handleReset(true);
    logEvent(analytics, 'reading_session_abort');
  };

  const handleReset = (reset) => {
    setGlobalState({
      ...globalState,
      isRunning: false,
      activeSession: false,
      manualSession: false,
      duration: 0,
      sessionDuration: 0,
      startTime: null,
      endPage: reset ? 0 : endPage,
      share: {},
    });

    setDate(moment(yesterday).format('YYYY-MM-DD'));
    setTime(moment(yesterday).set({ hour: 22, minute: 0 }).format('HH:mm'));
    setSpeed(parseInt(readSpeed.medium));
    setQuote('');
    setSummary('');
    setReview('');
    setRating('');
    setSaving(false);
  };

  const addSession = async (bookID, userID, session, endPage, time) => {
    setSaving(true);
    let bookCompleted = 0;
    logEvent(analytics, 'reading_session_end');

    try {
      // Update UserBooks collection
      addSessionToBook(
        bookID,
        userID,
        session,
        endPage,
        time,
        user,
        currentBook,
        review,
        rating,
        fetchUserData,
        bookCompleted,
        showNotification,
        checkAchievements,
        t,
        fetchBooks,
        isManual,
        setGlobalState,
        globalState,
      );
    } catch (error) {
      console.error('Error adding session: ', error);
    } finally {
      if (parseInt(currentBook.number_of_pages) === parseInt(endPage)) {
        bookCompleted = 1;
      }
      setGlobalState({
        ...globalState,
        setShowShareCard: true,
      });
    }
  };

  const handleSubmit = async () => {
    const newSession = {
      date: isManual ? new Date(date) : new Date(),
      duration: isManual ? manualDuration : time,
      pages: endPage - (currentBook.pages ? currentBook.pages : 0),
      quote: quote,
      summary: summary,
    };

    addSession(
      currentBook.bookID,
      user.uid,
      newSession,
      endPage,
      isManual ? manualDuration : time,
    );
  };

  const updatePage = (e) => {
    if (e.target.value < currentBook.pages) {
      setGlobalState({
        ...globalState,
        endPage: parseInt(currentBook.pages),
      });
      return false;
    }
    setGlobalState({
      ...globalState,
      endPage: parseInt(e.target.value),
    });
  };

  const stepPageLess = () => {
    if (endPage <= currentBook.pages) {
      return false;
    }
    setGlobalState({
      ...globalState,
      endPage: endPage - 1,
    });
  };

  const stepPageMore = () => {
    if (endPage >= currentBook.number_of_pages) {
      return false;
    }
    setGlobalState({
      ...globalState,
      endPage: endPage + 1,
    });
  };

  const root = document.documentElement;
  // Fix for displaying input range slider progress
  useEffect(() => {
    root.style.setProperty(
      '--slider-percent',
      `${(100 * endPage) / currentBook.number_of_pages}%`,
    );
  }, [endPage, currentBook.number_of_pages]);

  const coverUrl =
    typeof currentBook.cover_url !== 'undefined' && currentBook.cover_url[0]
      ? updateProtocol(currentBook.cover_url[0])
      : bookCover;

  return [
    (isActive || isManual) && !isRunning && (
      <div key="overlay" className="fullscreen-overlay">
        <div className="fullscreen-container" tabIndex={-1}>
          <button
            className="fullscreen-close"
            onClick={isManual ? () => handleReset(true) : handleStart}
          >
            <img
              src={isManual ? btnClose : arrowLeft}
              width={42}
              height={42}
              alt="Back icon"
            />
            {isManual ? t.cancelmanualsession : t.resumesession}
          </button>

          <div className="card-wrapper">
            <div className="card">
              <div className="card-medium">
                <img
                  src={coverUrl}
                  alt={currentBook.title}
                  height={120}
                  width={80}
                  className="card-medium-image"
                />
                <BookInfo book={currentBook} t={t} />
              </div>
            </div>
          </div>

          {isManual && (
            <div className="form">
              <h2 className="fs-l form-header">{t.manualsessionduration}</h2>
              <div className="btn-group-two-col">
                <div className="session-info">
                  <div className="date-time-wrapper">
                    <label htmlFor="date" className="form-label pt-65">
                      <img
                        src={iconCalendar}
                        height={18}
                        width={18}
                        alt="input icon"
                      />
                      {t.sessiondate}
                    </label>
                    <input
                      id="date"
                      type="date"
                      value={date || ''}
                      className="btn btn-primary w-100"
                      onInput={handleDateChange}
                      inputMode="numeric"
                      max={moment(now).format('YYYY-MM-DD')}
                    />
                  </div>
                </div>

                <div className="session-info">
                  <div className="date-time-wrapper">
                    <label htmlFor="time" className="form-label pt-65">
                      <img
                        src={iconClock}
                        height={18}
                        width={18}
                        alt="input icon"
                      />
                      {t.sessiontime}
                    </label>
                    <input
                      id="time"
                      type="time"
                      value={sessionTime || ''}
                      className="btn btn-primary w-100"
                      onInput={handleTimeChange}
                      inputMode="numeric"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="form">
            <h2 className="fs-l form-header">{t.sessionduration}</h2>
            <div className="session-info">
              <div className="time">
                {formatTime(isManual ? manualDuration : time)}
              </div>

              {isManual && !hasPageSpeed && <div className="separator"></div>}

              {isManual && !hasPageSpeed && (
                <div className="date-time-wrapper">
                  <span className="form-label pt-65">
                    <img
                      src={iconSpeed}
                      height={18}
                      width={18}
                      alt="Read speed icon"
                    />
                    {t.selectpagespeed}
                  </span>
                  <div className="readspeed">
                    <button
                      className={`btn ${readSpeed.slow === speed ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={() => handleSpeedChange(readSpeed.slow)}
                    >
                      {t.slow}
                    </button>
                    <button
                      className={`btn ${readSpeed.medium === speed ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={() => handleSpeedChange(readSpeed.medium)}
                    >
                      {t.medium}
                    </button>
                    <button
                      className={`btn ${readSpeed.fast === speed ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={() => handleSpeedChange(readSpeed.fast)}
                    >
                      {t.fast}
                    </button>
                  </div>
                </div>
              )}

              {isManual && <div className="separator"></div>}

              {isManual && (
                <div className="fs-l">
                  <span className="pt-65">
                    {hasPageSpeed ? t.sessionavgspeed : t.sessionbasedon}:{' '}
                  </span>
                  {hasPageSpeed ? pagesPerHour : speed} {t.pagesperhour}
                </div>
              )}
            </div>
          </div>

          <div className="form">
            <h2 className="fs-l form-header">{t.updatepagesMessage}</h2>
            <div className="session-info">
              <div className="stepper">
                <div className="session-pages">
                  <span className="fs-l">
                    {t.page} {endPage && endPage.toLocaleString()}
                  </span>
                  <span className="fs-l pt-65">
                    {` ${t.of} `}
                    {currentBook.number_of_pages &&
                      currentBook.number_of_pages.toLocaleString()}
                  </span>
                </div>
              </div>

              <div className="separator"></div>

              <div className="stepper">
                <button
                  className={`btn-icon btn-stepper-left ${endPage <= currentBook.pages ? 'pt-65' : ''}`}
                  onClick={stepPageLess}
                  disabled={endPage <= currentBook.pages}
                >
                  <img src={btnLeft} width={32} height={32} alt="Minus icon" />
                </button>

                <input
                  className="slider"
                  type="range"
                  id="pages"
                  name="pages"
                  min={0}
                  max={currentBook.number_of_pages}
                  value={endPage}
                  onInput={updatePage}
                  onChange={updatePage}
                />

                <button
                  className={`btn-icon btn-stepper-right ${endPage >= currentBook.number_of_pages ? 'pt-65' : ''}`}
                  onClick={stepPageMore}
                  disabled={endPage >= currentBook.number_of_pages}
                >
                  <img src={btnRight} width={32} height={32} alt="Plus icon" />
                </button>
              </div>

              <div className="separator"></div>

              <div className="fs-l">
                <span className="pt-65">{t.pagesreadMessage}: </span>
                {(endPage - currentBook.pages || 0).toLocaleString()}
              </div>
            </div>
          </div>

          <SessionForm
            quote={quote}
            summary={summary}
            review={review}
            rating={rating}
            handleQuoteChange={handleQuoteChange}
            handleSummaryChange={handleSummaryChange}
            handleReviewChange={handleReviewChange}
            setRating={setRating}
            showReview={endPage === currentBook.number_of_pages}
            t={t}
          />

          <div className="btn-group-wrapper">
            <div
              className={`btn-group ${isActive ? 'btn-group-two-col' : null}`}
            >
              {isActive && (
                <button
                  className="btn btn-secondary w-100"
                  onClick={handleStart}
                  disabled={saving}
                >
                  {t.resumesession}
                </button>
              )}
              <button
                disabled={
                  endPage === currentBook.pages ||
                  endPage === 0 ||
                  saving ||
                  (isManual &&
                    (manualDuration === 0 || date === '' || sessionTime === ''))
                }
                className="btn btn-primary w-100"
                onClick={handleSubmit}
              >
                {endPage === currentBook.number_of_pages
                  ? t.finishbook
                  : isManual
                    ? t.manualfinishsession
                    : t.finishsession}
              </button>
            </div>
            <button
              className="btn btn-borderless w-100"
              onClick={handleClickReset}
              disabled={saving}
            >
              {isManual ? t.cancelmanualsession : t.cancelsession}
            </button>
          </div>
        </div>
      </div>
    ),
    <Session
      key="session"
      noCurrentBook={noCurrentBook}
      isRunning={isRunning}
      isActive={isActive || isManual}
      handleStop={handleStop}
      handleStart={handleStart}
      time={time}
      t={t}
      currentBook={currentBook}
      sessions={sessions}
      weekstart={weekstart}
    />,
    share?.showShareCard && share?.pagesRead && share?.secondsRead && (
      <ShareableCard
        key="sharecard"
        handleReset={handleReset}
        globalState={globalState}
        quote={quote}
        summary={summary}
        review={review}
        rating={rating}
        user={user}
      />
    ),
  ];
};

export default TopBar;
